import React, { useCallback, useState } from 'react';
import { Button, Dialog, Grid, TextField, Typography } from '@mui/material';
import { StepInterface, useActivitiesContext } from 'pages/Activities/activities.context';
import { useSnackbarContext } from 'context/snackbar.context';
import styled from 'styled-components';
import SingleSelectDropdown from 'components/DropdownSelect/SingleSelectDropdown/SingleSelectDropdown';
import { DropdownOption } from 'components/DropdownSelect/dropdown.types';
import { formatDate, formatMinutes, getUsersName } from 'utils/formatting';
import SectionHeader from 'components/SectionHeader/SectionHeader';
import { Tag } from 'components/Tag/Tag';
import { themeOptions } from 'theme';
import { OverviewDetailInterface } from 'components/Overview/overview.types';
import { ErrorTextWrapper } from 'pages/Campaigns/UpdateCampaign';
import { hasPermissions } from 'config/ability';
import { useAuthContext } from 'context/auth.context';
import {
  ActivityAssignmentData,
  StepWithResponseData,
} from 'hooks/activities/useQueryActivityAssignments';
import useMutationCompleteAssignmentStep from 'hooks/activities/useMutationCompleteAssignmentStep';
import OverviewDetail from 'components/Overview/OverviewDetail/OverviewDetail';

const DropdownWrapper = styled.div`
  margin-top: -2rem;
  min-width: 9rem;
  width: 100%;

  .MuiAutocomplete-root {
    width: 100%;
  }
`;

interface CompleteStepInputInterface {
  id: number;
  completedDate: string;
  actualTimeToComplete?: number;
  answer?: string;
}

export interface CompleteStepPropInterface {
  activityAssignment: ActivityAssignmentData;
  stepWithResponse: StepWithResponseData;
  stepNumber?: number;
  totalSteps?: number;
}

const CompleteAssignmentStep = ({
  activityAssignment,
  stepWithResponse,
  stepNumber,
  totalSteps,
}: CompleteStepPropInterface) => {
  const [showCompleteStepModal, setShowCompleteStepModal] = useState<boolean>(false);
  const [actualTimeToCompleteUnit, setActualTimeToCompleteUnit] = useState<string>('Minute(s)');
  const { displaySnackbarMessage, displayApiResult } = useSnackbarContext();
  const { refetchActivities } = useActivitiesContext();
  const { ability, user, userTenantId } = useAuthContext();
  const [completeAssignmentStep] = useMutationCompleteAssignmentStep();
  const [stepToComplete, setStepToComplete] = useState<CompleteStepInputInterface>({
    id: stepWithResponse.step.id,
    completedDate: new Date().toISOString().slice(0, 10),
  });

  const timeUnitOptions = [
    { id: 1, name: 'Minute(s)' },
    { id: 2, name: 'Hour(s)' },
  ];

  const handleCompleteStep = useCallback(async () => {
    const step = {
      activityAssignmentId: activityAssignment.id,
      stepId: stepWithResponse.step.id,
      stepResponseId: stepWithResponse.response?.id,
      completedDate: new Date().toISOString().slice(0, 10),
      actualTimeToComplete: stepToComplete.actualTimeToComplete,
      answer: stepToComplete.answer,
    };

    if (step.actualTimeToComplete && actualTimeToCompleteUnit === 'Hour(s)') {
      step.actualTimeToComplete = step.actualTimeToComplete * 60;
    }

    const result = await completeAssignmentStep({
      variables: {
        input: {
          ...step,
        },
      },
    }).then(
      (result) => {
        result.data?.completeAssignmentStep &&
          displayApiResult(result.data.completeAssignmentStep, 'Step Completed');
        refetchActivities();
      },
      (rejection) => {
        displaySnackbarMessage(rejection.message, 'error');
      }
    );

    setShowCompleteStepModal(false);
    return result;
  }, [
    activityAssignment.id,
    actualTimeToCompleteUnit,
    completeAssignmentStep,
    displayApiResult,
    displaySnackbarMessage,
    refetchActivities,
    stepToComplete.actualTimeToComplete,
    stepToComplete.answer,
    stepWithResponse.response?.id,
    stepWithResponse.step.id,
  ]);

  // Return null if user does not have 'Complete' permissions
  if (
    !hasPermissions(ability, 'complete', 'Step', { tenantId: userTenantId }) &&
    !hasPermissions(ability, 'complete', 'Step', { stepId: stepWithResponse.step.id }) &&
    !hasPermissions(ability, 'complete', 'Step', {
      userId: user?.user?.id,
    }) &&
    !hasPermissions(ability, 'complete', 'StepResponse', { stepResponseId: stepWithResponse.id })
  ) {
    return null;
  }

  const handleChangeField = (value: string | number, key: keyof StepInterface) =>
    setStepToComplete({ ...stepToComplete, [key]: value });

  const handleUpdateTimeUnit = (unit: string) => {
    setActualTimeToCompleteUnit(unit);
  };

  const getSelected = (unitOfTime?: string) =>
    timeUnitOptions.find((option) => option.name === unitOfTime);

  const stepDetails = (stepWithResponse: StepWithResponseData): OverviewDetailInterface[] => [
    {
      title: 'Assigned To',
      value: (
        <>
          {stepWithResponse.response?.assignedUser
            ? getUsersName(stepWithResponse.response.assignedUser)
            : 'N/A'}
        </>
      ),
      md: 4,
    },
    { title: 'Description', value: stepWithResponse.step.description, md: 8, richText: true },
    {
      title: 'Start Date',
      value: formatDate(stepWithResponse.step.startDate),
      hide: !stepWithResponse.step.startDate,
      md: 4,
    },
    {
      title: 'Estimated Time to Complete',
      value: formatMinutes(stepWithResponse.step.estimatedTimeToComplete),
      hide: typeof stepWithResponse.step.estimatedTimeToComplete === 'undefined',
      md: 8,
    },
    {
      title: 'Due Date',
      value: formatDate(stepWithResponse.step.dueDate),
      hide: !stepWithResponse.step.dueDate,
      md: 4,
    },
    {
      title: 'Actual Time to Complete',
      value: (
        <>
          {stepWithResponse.response?.actualTimeToComplete ? (
            formatMinutes(stepWithResponse.response?.actualTimeToComplete)
          ) : (
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <TextField
                  id='outlined-number'
                  type='number'
                  onChange={(actualTimeToComplete) =>
                    handleChangeField(
                      Number(actualTimeToComplete.target.value),
                      'actualTimeToComplete'
                    )
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <DropdownWrapper>
                  <SingleSelectDropdown
                    variant='outlined'
                    allOptions={timeUnitOptions}
                    selected={getSelected(actualTimeToCompleteUnit)}
                    setSelected={(selectedOption?: DropdownOption) =>
                      handleUpdateTimeUnit(selectedOption?.name ?? '')
                    }
                    disableClearable={true}
                  />
                </DropdownWrapper>
              </Grid>
            </Grid>
          )}
        </>
      ),
      hide: typeof stepWithResponse.step.estimatedTimeToComplete === 'undefined',
      md: 8,
    },
  ];

  return (
    <>
      {stepWithResponse.response?.completedDate ? (
        <> {formatDate(stepWithResponse.response.completedDate)} </>
      ) : (
        stepWithResponse && (
          <Button
            onClick={() => setShowCompleteStepModal(true)}
            variant='contained'
            color='secondary'
          >
            Complete Step
          </Button>
        )
      )}
      <Dialog open={showCompleteStepModal} onClose={() => setShowCompleteStepModal(false)}>
        <Grid container padding='1rem'>
          <Grid item xs={12} mb={2}>
            {(stepNumber ||
              stepWithResponse.step.requiresReview ||
              stepWithResponse.step.isRequired) && (
              <SectionHeader
                containerPadding='0'
                alignItems='flex-start'
                bold
                title={
                  stepNumber && (
                    <Typography>
                      Step {stepNumber} of {totalSteps}
                    </Typography>
                  )
                }
                actions={
                  <>
                    {stepWithResponse.step.requiresReview && (
                      <Tag
                        text='Review Required'
                        color={themeOptions.colors.warning}
                        fontWeight={500}
                        padding='0.25rem 0.625rem'
                      />
                    )}
                    &nbsp;
                    {stepWithResponse.step.isRequired && (
                      <Tag
                        text='Step Required'
                        color={themeOptions.colors.error900}
                        fontWeight={500}
                        padding='0.25rem 0.625rem'
                      />
                    )}
                  </>
                }
              />
            )}
          </Grid>
          <Grid item xs={12}>
            <Typography variant='h3' component='span' sx={{ fontWeight: 400, color: 'black' }}>
              {stepWithResponse.step.subject}
            </Typography>
            <Grid container mt={3}>
              {stepDetails(stepWithResponse).map((detail) => (
                <OverviewDetail
                  key={`${(typeof detail.value).toString()}-${detail.title}`}
                  {...detail}
                />
              ))}
            </Grid>
          </Grid>
          <Grid item xs={12} mt={5}>
            <Button
              disabled={
                !!stepWithResponse.step.question &&
                !!stepWithResponse.step.isRequired &&
                !stepToComplete.answer
              }
              onClick={handleCompleteStep}
              variant='contained'
            >
              Complete Step
            </Button>
            &nbsp;&nbsp;
            <Button onClick={() => setShowCompleteStepModal(false)}>Cancel</Button>
          </Grid>
        </Grid>
        {!!stepWithResponse.step.question &&
          !!stepWithResponse.step.isRequired &&
          !stepToComplete.answer && <ErrorTextWrapper>* Answer is Required</ErrorTextWrapper>}
      </Dialog>
    </>
  );
};

export default CompleteAssignmentStep;
